import {inject, Injectable, NgZone} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {StateControllerService} from '../../api';
import {SseEvent} from './sse-event.model';
import {TokenRefreshService} from '../auth/token-refresh.service';

@Injectable({
  providedIn: 'root'
})
export class SseService {
  private stateControllerService = inject(StateControllerService);
  private readonly zone = inject(NgZone);
  private readonly tokenRefresh = inject(TokenRefreshService);

  private readonly reconnectedSubject = new Subject<string>();

  /**
   * Feuert, wenn ein Strom nach einem Abbruch WIEDER steht - mit dem Pfad des Stroms.
   *
   * <p>Notwendig, weil {@link #listen} still neu verbindet: der Server beendet den SSE-Strom nach
   * einigen Minuten selbst (im Backend-Log als {@code AsyncRequestTimeoutException} sichtbar), und
   * die Schleife unten baut ihn neu auf, ohne dass ein Abonnent davon erfaehrt. Ereignisse, die in
   * dieses Fenster fallen, sind verloren - unwiederbringlich, denn der Strom hat kein Nachholen.
   * Wer Zustand aus Ereignissen fortschreibt, muss ihn danach also einmal frisch holen; ohne dieses
   * Signal blieben etwa Loeschzeilen dauerhaft auf "wird geloescht" stehen, obwohl serverseitig
   * langst alles durch war.</p>
   */
  public readonly reconnected$ = this.reconnectedSubject.asObservable();

  private get basePath(): string {
    return (this.stateControllerService as any).basePath ?? '';
  }

  /**
   * Opens an SSE stream using fetch() so the Authorization header
   * (Bearer token from localStorage) can be included.
   * Uses the same basePath as existing API services.
   *
   * <p>Das Token kommt vor jedem (Neu-)Verbinden aus {@link TokenRefreshService#getValidToken}, ist
   * also frisch. Antwortet der Server trotzdem mit 401, wird einmal refresht und neu verbunden;
   * scheitert der Refresh, hat der TokenRefreshService bereits abgemeldet und der Strom endet mit
   * einem Fehler. Ein zweites 401 direkt nach dem Refresh ist ein echter Fehler.</p>
   *
   * <p>Die Ereignisse werden ueber {@link NgZone#run} ausgeliefert, und das ist load-bearing: der
   * Strom laeuft ueber {@code fetch()} und {@code reader.read()}, und ReadableStream wird von
   * zone.js NICHT gepatcht (anders als {@code EventSource}). Alles hinter dem {@code await} landet
   * damit ausserhalb der Angular-Zone - die Abonnenten aktualisieren ihre Felder, aber es laeuft
   * keine Change Detection. Sichtbar wurde das am Loesch-Dialog: er blieb auf 0 stehen, waehrend die
   * Badge daneben "mitzaehlte" - die aktualisierte sich nur, weil das Darueberfahren mit der Maus
   * eine Change Detection ausloeste und dann die laengst gesetzten Werte rendern liess.</p>
   */
  listen<T>(path: string): Observable<SseEvent<T>> {
    return new Observable<SseEvent<T>>(observer => {
      let abortController = new AbortController();
      let active = true;
      let retryDelay = 3000;
      // Ob dieser Strom schon einmal gestanden hat. Nur dann ist ein Verbinden ein WIEDER-Verbinden,
      // und nur dann kann etwas verpasst worden sein.
      let hadConnection = false;
      // Ob der letzte Versuch schon ein 401 mit anschliessendem Refresh war (siehe oben).
      let refreshedAfterUnauthorized = false;

      const connect = async () => {
        while (active) {
          abortController = new AbortController();
          const url = `${this.basePath}${path}`;

          try {
            // Wirft, wenn der Refresh das Backend nicht erreicht – dann greift der Backoff unten.
            const token = await this.tokenRefresh.getValidToken();
            if (!active) return;
            if (!token) {
              observer.error('SSE connection failed: nicht angemeldet');
              return;
            }
            const response = await fetch(url, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'text/event-stream'
              },
              signal: abortController.signal
            });

            if (response.status === 401 && !refreshedAfterUnauthorized) {
              refreshedAfterUnauthorized = true;
              if (await this.tokenRefresh.refreshAfterUnauthorized(token)) {
                continue;
              }
              observer.error('SSE connection failed: Sitzung abgelaufen');
              return;
            }

            if (!response.ok || !response.body) {
              // Non-2xx and non-503 (timeout) are real errors — stop retrying
              if (response.status !== 503) {
                observer.error(`SSE connection failed: ${response.status}`);
                return;
              }
              // 503 = server-side SSE timeout, reconnect after delay
              await new Promise(r => setTimeout(r, retryDelay));
              continue;
            }

            retryDelay = 3000; // reset backoff on successful connect
            refreshedAfterUnauthorized = false;
            if (hadConnection) {
              // Zurueck in die Angular-Zone, aus demselben Grund wie bei den Ereignissen (s.o.).
              this.zone.run(() => this.reconnectedSubject.next(path));
            }
            hadConnection = true;
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (active) {
              const {done, value} = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, {stream: true});
              const lines = buffer.split('\n');
              buffer = lines.pop() ?? '';

              for (const line of lines) {
                if (line.startsWith('data:')) {
                  const json = line.slice(5).trim();
                  if (json) {
                    try {
                      const event = JSON.parse(json) as SseEvent<T>;
                      // Zurueck in die Angular-Zone, sonst rendert niemand das Ergebnis (s.o.).
                      this.zone.run(() => observer.next(event));
                    } catch {
                      // ignore malformed JSON
                    }
                  }
                }
              }
            }

            // Stream ended (server closed / timeout) — reconnect
            if (active) {
              await new Promise(r => setTimeout(r, retryDelay));
            }
          } catch (err: any) {
            if (err?.name === 'AbortError') return;
            // Network error — reconnect with backoff
            retryDelay = Math.min(retryDelay * 2, 30000);
            await new Promise(r => setTimeout(r, retryDelay));
          }
        }
      };

      connect();

      return () => {
        active = false;
        abortController.abort();
      };
    });
  }
}
